import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Checkbox,
} from '@headlessui/react';
import cls from './select.module.scss';
import { SelectProps, Option } from './select.types';

export const Select = (props: SelectProps) => {
  const { optionsList, placeholder = 'Select...', errorText } = props;

  if (props.isMulti) {
    return (
      <MultiSelect
        optionsList={optionsList}
        selected={props.selected}
        onChange={props.onChange}
        toBeSelected={props.toBeSelected}
        placeholder={placeholder}
        errorText={errorText}
      />
    );
  }

  return (
    <SingleSelect
      optionsList={optionsList}
      selected={props.selected}
      onChange={props.onChange}
      placeholder={placeholder}
      errorText={errorText}
    />
  );
};

interface SingleSelectInternalProps {
  optionsList: Option[];
  selected: Option | null;
  onChange: (selected: Option) => void;
  placeholder: string;
  errorText?: string;
}

const SingleSelect = ({
  optionsList,
  selected,
  onChange,
  placeholder,
  errorText,
}: SingleSelectInternalProps) => {
  return (
    <Listbox
      as='div'
      value={selected}
      onChange={onChange}
      by='value'
      className={cls.selectWrapper}
    >
      {({ open }) => (
        <>
          <ListboxButton
            className={`${cls.trigger} ${open ? cls.open : ''} ${errorText ? cls.error : ''}`}
          >
            <span
              className={selected?.value ? cls.triggerText : cls.placeholder}
            >
              {selected?.value ? selected.label : placeholder}
            </span>
            <ChevronIcon open={open} />
          </ListboxButton>
          <ListboxOptions
            anchor={{ to: 'bottom start', padding: '16px' }}
            className={cls.options}
          >
            {optionsList.map(option => (
              <ListboxOption
                key={option.value}
                value={option}
                className={cls.option}
              >
                <span className={cls.optionLabel}>{option.label}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
          {errorText && <p className={cls.errorText}>{errorText}</p>}
        </>
      )}
    </Listbox>
  );
};

interface MultiSelectInternalProps {
  optionsList: Option[];
  selected: Option[];
  onChange: (selected: Option[]) => void;
  toBeSelected?: number;
  placeholder: string;
  errorText?: string;
}

const MultiSelect = ({
  optionsList,
  selected,
  onChange,
  toBeSelected,
  placeholder,
  errorText,
}: MultiSelectInternalProps) => {
  const selectedValues = new Set(selected.map(s => s.value));
  const limitReached =
    toBeSelected !== undefined && selected.length >= toBeSelected;

  const renderTriggerText = () => {
    if (selected.length === 0) return placeholder;
    if (toBeSelected !== undefined)
      return `${selected.length} / ${toBeSelected} selected`;
    return `${selected.length} selected`;
  };

  return (
    <Listbox
      as='div'
      value={selected}
      onChange={onChange}
      by='value'
      multiple
      className={cls.selectWrapper}
    >
      {({ open }) => (
        <>
          <ListboxButton
            className={`${cls.trigger} ${open ? cls.open : ''} ${errorText ? cls.error : ''}`}
          >
            <span
              className={
                selected.length > 0 ? cls.triggerText : cls.placeholder
              }
            >
              {renderTriggerText()}
            </span>
            <ChevronIcon open={open} />
          </ListboxButton>
          <ListboxOptions
            anchor={{ to: 'bottom start', padding: '16px' }}
            className={cls.options}
          >
            {optionsList.map(option => {
              const isSelected = selectedValues.has(option.value);
              const isDisabled = limitReached && !isSelected;

              return (
                <ListboxOption
                  key={option.value}
                  value={option}
                  disabled={isDisabled}
                  className={`${cls.option} ${cls.multiOption} ${isDisabled ? cls.disabled : ''}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onClick={() =>
                      onChange(
                        isSelected
                          ? selected.filter(s => s.value !== option.value)
                          : [...selected, option]
                      )
                    }
                    className={`${cls.checkbox} ${isSelected ? cls.checked : ''}`}
                  >
                    {isSelected && (
                      <svg
                        className={cls.checkIcon}
                        viewBox='0 0 14 14'
                        fill='none'
                      >
                        <path
                          d='M11.6666 3.5L5.24992 9.91667L2.33325 7'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </Checkbox>
                  <span className={cls.optionLabel}>{option.label}</span>
                </ListboxOption>
              );
            })}
          </ListboxOptions>
          {errorText && <p className={cls.errorText}>{errorText}</p>}
        </>
      )}
    </Listbox>
  );
};

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`${cls.chevron} ${open ? cls.chevronOpen : ''}`}
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M5 7.5L10 12.5L15 7.5'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
